import React, { useState } from "react";
import { Button, FlashMessage } from "../../common";
import Image from "../../common/Image";
import { useDispatch } from "react-redux";
import { sendInvitationRequest } from "../../../reducers/SearchAndConnect/searchAndConnect.reducer";
import appRoutes from "../../../routes/app.routes";
import { getRemainingResendHours } from "../../../utils/helper";

interface ConnectionCardProps {
  index: number;
  recommendation?: any; // Optional recommendation data
  connectionStatus?: any; // Connection status for this recommendation
  onConnectionUpdate?: (id: number, status: any) => void; // Callback to update connection status
  onProfileRemove?: (id: number) => void; // Callback to remove profile
}

function ConnectionCard({
  index,
  recommendation,
  connectionStatus,
  onConnectionUpdate,
  onProfileRemove,
}: ConnectionCardProps) {
  const dispatch = useDispatch();
  const [isConnecting, setIsConnecting] = useState(false);

  // Extract data from recommendation based on the new JSON structure
  // The data is now directly accessible from recommendation
  const firstName = recommendation?.first_name || "";
  const lastName = recommendation?.last_name || "";
  const profileImage = recommendation?.profile_image || "";

  const candidateProfile = recommendation?.candidateProfile || {};
  const jobTitle =
    candidateProfile?.jobTitle?.title ||
    candidateProfile?.job_title ||
    "Professional";
  const location =
    candidateProfile?.location?.location ||
    candidateProfile?.job_location ||
    "Location not specified";
  const workStatus =
    candidateProfile?.work_status || "Work status not specified";

  // Determine button state based on connection status
  const getButtonState = () => {
    if (!connectionStatus) {
      return { text: "Connect", disabled: false, variant: "outlined" as const };
    }

    switch (connectionStatus.status_text) {
      case "pending":
        return {
          text: "Request Sent",
          disabled: true,
          variant: "outlined" as const,
        };
      case "accepted":
        return {
          text: "Connected",
          disabled: true,
          variant: "outlined" as const,
        };
      case "rejected":
        return {
          text: "Rejected",
          disabled: false,
          variant: "outlined" as const,
        }; // Allow retry
      case "cancelled_24_hours": {
        const remainingHours = getRemainingResendHours(
          connectionStatus?.cancelled_at
        );
        return {
          text: `Wait ${remainingHours} ${remainingHours === 1 ? "hour" : "hours"}`,
          disabled: true,
          variant: "outlined" as const,
        };
      }
      default:
        return {
          text: "Connect",
          disabled: false,
          variant: "outlined" as const,
        };
    }
  };

  const buttonState = getButtonState();

  const handleConnect = async (e?: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    if (!recommendation?.id) {
      FlashMessage("Unable to send connection request", "error");
      return;
    }

    setIsConnecting(true);
    try {
      // Check if there's an existing connection to update
      const existingConnection = connectionStatus;

      if (existingConnection && existingConnection.connect_id) {
        // Update existing connection - send connect_id
        dispatch(
          sendInvitationRequest({
            note: "",
            id: recommendation.id,
            connect_id: existingConnection.connect_id,
          })
        );

        FlashMessage("Connection request updated successfully!", "success");
      } else {
        // Send new connection request
        dispatch(
          sendInvitationRequest({
            note: "",
            id: recommendation.id,
          })
        );

        FlashMessage("Connection request sent successfully!", "success");
      }

      // Update local connection status to pending
      if (onConnectionUpdate) {
        const pendingStatus = {
          connect_id: existingConnection?.connect_id || null,
          status_id: 1,
          status_text: "pending",
        };
        onConnectionUpdate(recommendation.id, pendingStatus);
      }
    } catch (error) {
      FlashMessage("Failed to send connection request", "error");
    } finally {
      setIsConnecting(false);
    }
  };

  const handleProfileRemove = (e?: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    if (!recommendation?.id) {
      FlashMessage("Unable to remove profile", "error");
      return;
    }

    if (onProfileRemove) {
      onProfileRemove(recommendation.id);
    }
  };

  const handleCardClick = () => {
    window.open(appRoutes.candidatePublicProfile.generateFullPath(recommendation.slug), "_blank");
  };

  return (
    <div key={index} className="suggested-card" onClick={handleCardClick}>
      <div className="card-head">
        <div className="profile-img">
          <Image src={profileImage} alt={`${firstName} ${lastName}`} />
        </div>
        <button
          className="remove-profile-btn"
          onClick={handleProfileRemove}
          title="Remove profile"
        >
          <svg
            width="6"
            height="6"
            viewBox="0 0 6 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.47319 0.523468L0.523438 5.47322M5.47319 5.47319L0.523438 0.523438"
              stroke="white"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
      <div className="card-body">
        <h6>
          {firstName} {lastName}
        </h6>
        <p>{jobTitle}</p>
        <p className="location">{location}</p>
        <p className="work-status">{workStatus}</p>
      </div>
      <div className="card-footer">
        <Button
          variant={buttonState.variant}
          className={`outlined ${connectionStatus?.status_text || ""}`}
          color="none"
          onClick={handleConnect}
          disabled={buttonState.disabled || isConnecting}
        >
          {isConnecting ? "Sending..." : buttonState.text}
        </Button>
      </div>
    </div>
  );
}

export default ConnectionCard;
